import React, { useState, useEffect } from 'react';

const Solutions = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Farmnet enables smart farming—helping smallholder farmers adopt digital tools and data-driven practices for more productive and sustainable agriculture.";

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setIsTyping(false);
          setTimeout(() => {
            backspaceText();
          }, 2000);
        }, 1000);
      }
    };

    const backspaceText = () => {
      let backspaceInterval;
      let backspaceIndex = fullText.length;

      const eraseText = () => {
        if (backspaceIndex >= 0) {
          setText(fullText.substring(0, backspaceIndex));
          backspaceIndex--;
        } else {
          clearInterval(backspaceInterval);
          setIsTyping(true);
          currentIndex = 0;
          typingInterval = setInterval(typeText, 50);
        }
      };

      backspaceInterval = setInterval(eraseText, 20);
    };

    if (isTyping) {
      typingInterval = setInterval(typeText, 50);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isTyping, fullText]);

  return (
    <div className="relative bg-[#F3F3F3]">
      {/* Content */}
      <div className="relative z-10 px-4 md:px-24 pb-10 md:pt-5 pt-1">
        {/* Heading */}
        <div className="mt-2 md:mt-2 md:mb-5 flex justify-between items-center p mb-2">
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 ">
          <span className='text-[#152C24]'>INSIGHTS & INNOVATIONS</span>
          </h1>
          <div>
            <div className='flex md:gap-2 gap-1 justify-center md:mt-2'>
              <div className='bg-[#CFD7D7] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
              <div className="bg-[#2E453D] border-b-2 border-[#FFBD26] md:ml-0 ml-2 text-white font-normal md:px-6 px-2 rounded-md md:rounded-[30px] w-[70px] h-[15px] md:w-[150px] md:text-[20px] text-[6px] md:h-[40px] mt-0 md:-mt-[1px] flex justify-between items-center">
                See More
              </div>
              <div className='bg-[#2E453D] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 mr-2 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
            </div>
          </div>
        </div>

        {/* Records Section */}
        <div className="rounded-md w-full md:h-[630px] h-[175px] relative flex items-center justify-center p-2 md:p-0 pt-2 md:mt-4">
          <div className="rounded-xl overflow-hidden relative w-full h-[180px] md:mt-5 md:h-[700px] flex justify-center items-center cursor-pointer">
              <>
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751380763/Rectangle_34641497_lji0ej.png"
                  alt="Smart Farming"
                  className="object-cover w-full h-full"
                />
              </>
          </div>
        </div>

        {/* Description */}
        <div className='md:text-[32px] text-[10px] font-normal md:mt-16 mt-2 space-y-6'>
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 text-[#979797]">
            INSIGHTS & INNOVATIONS | <span className='text-[#152C24]'>Smart Farming</span>
          </h1>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🌾 What Is Smart Farming?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Smart farming is the use of modern technologies and data-driven tools—such as mobile apps, sensors, GPS, drones, and AI—to make farming more efficient, productive, and sustainable. It's also known as precision agriculture, and it allows farmers to make informed decisions based on real-time information about their soil, crops, weather, and livestock.
          </p>
          
          <p className="md:text-[32px] text-[8px]">
            Smart farming is not just for large-scale commercial farms. Thanks to mobile technology and affordable tools, smallholder farmers in Africa are increasingly adopting smart farming practices.
          </p>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            💡 Core Features of Smart Farming
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Smart farming typically includes:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Data collection from the farm (e.g., soil sensors, weather stations)</li>
            <li>Digital advisory (e.g., mobile apps, AI-driven recommendations)</li>
            <li>Automation and remote control (e.g., drip irrigation systems, drones)</li>
            <li>Real-time decision-making based on data</li>
            <li>Sustainable resource use (e.g., efficient water and fertilizer application)</li>
          </ul>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🌍 How Smallholder Farmers in Africa Use Smart Farming
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            While the level of technology may vary, here are practical ways smallholder farmers are embracing smart farming:
          </p>
          
          <ol className="md:text-[32px] text-[8px] list-decimal pl-5 space-y-3 [&>li::marker]:font-bold">
            <li>
              <span className="font-bold">Mobile Apps for Advisory Services</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Planting and harvesting tips</li>
                <li>Pest and disease diagnostics</li>
                <li>Fertilizer recommendations</li>
                <li>Weather updates</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Soil & Crop Monitoring Tools</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Low-cost soil sensors or mobile diagnostics help farmers:</li>
                <li>Know when and how much to irrigate or fertilize</li>
                <li>Track soil health and nutrient levels</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Digital Weather Forecasts</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Using mobile SMS or apps, farmers receive local weather alerts and rainfall forecasts to better time farm activities.</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Drone and Satellite Services (Through Cooperatives or NGOs)</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Drones are used for:</li>
                <li>Mapping farms</li>
                <li>Monitoring crop health</li>
                <li>Spotting pest or disease outbreaks</li>
                <li>These are usually offered by service providers or cooperatives on a shared basis</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Smart Irrigation Systems</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use solar-powered drip irrigation or automated systems that release water based on soil moisture data.</li>
                <li>Helps save water and improve yields.</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Livestock Health Monitoring</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use mobile-based digital veterinary tools to track animal health, vaccinations, and get expert advice.</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Digital Market Access</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers post harvests on platforms like Farmnet, Twiga, or Maano to sell directly to buyers, often at better prices.</li>
              </ul>
            </li>
          </ol>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            📱 Role of Farmnet in Supporting Smart Farming
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            The Farmnet App supports smallholder farmers in adopting smart farming by offering:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Location-specific digital advisory on crops and livestock</li>
            <li>Real-time weather alerts and disease warnings</li>
            <li>Access to digital veterinary and agronomy experts</li>
            <li>Training content on smart practices (e.g., irrigation, crop rotation)</li>
            <li>Connection to smart farming tools and partners (e.g., input providers, soil testing labs)</li>
          </ul>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            ✅ Benefits of Smart Farming for Smallholders
          </h2>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>🌿 Higher productivity with fewer inputs</li>
            <li>💧 Better resource use (e.g., water, fertilizer)</li>
            <li>📊 Data-driven decisions that reduce risk</li>
            <li>🐄 Healthier animals and better yields</li>
            <li>📈 Increased profitability and resilience</li>
          </ul>
          
          <p className="md:text-[32px] text-[8px]">
            Smart farming empowers smallholder farmers to overcome challenges like climate change, low yields, and market inefficiencies using affordable, digital-first tools that are already in their hands.
          </p>
        </div>
        
        <div className='flex justify-end items-center'>
          <div>
            <h1 className="text-[18px] md:text-[32px] font-bold mt-2 mb-4 text-black">
              Download App
            </h1>
            <div className="md:w-[450px]">
              <div className="flex flex-row justify-between space-x-2 items-center sm:space-y-0">
                <a href="https://play.google.com/store/apps/details?id=com.farmnet.africa&pcampaignid=web_share" alt="none">   
             <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751889690/google_PLAY-LOGO.WINE_emyxpu.png" className="h-[25px] md:w-[280px] md:h-[180px]" alt="App Store" />    
             <p className="md:text-[14px] text-[6px] md:-mt-12 md:pl-5 font-normal md:px-1">
             Click to download App
  </p>
     </a>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744414874/image_scynki.png"
                  alt="Google Play"
                  className="h-[30px] md:h-[75px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
        <div className='md:h-10 h-5'></div>
      </div>
    </div>
  );
};

export default Solutions;